import React, { createContext, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext();

// Hook to force dark mode on landing/marketing pages
export const useForcedDarkMode = () => {
  useEffect(() => {
    const root = window.document.documentElement;
    
    const force = () => {
      if (!root.classList.contains('dark') || root.classList.contains('light')) {
        root.classList.remove('light');
        root.classList.add('dark');
      }
    };
    force();
    // Provider effects run after child effects, so keep dark enforced while mounted
    const observer = new MutationObserver(force);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    
    return () => {
      observer.disconnect();
      // Read from localStorage at cleanup time (not capture time)
      // This ensures we get the correct preference even after navigation
      const savedTheme = localStorage.getItem('theme') || 'dark';
      root.classList.remove('light', 'dark');
      root.classList.add(savedTheme);
    };
  }, []);
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // 1. Check localStorage for saved preference
    const saved = localStorage.getItem('theme');
    if (saved) return saved;
    
    // 2. Check system preference (prefers-color-scheme)
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'light';
    }
    
    // 3. Default to dark (ISO Cyber-Fortress Night Mode)
    return 'dark';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Listen for system preference changes (when user hasn't manually set preference)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually toggled (check if they've used our toggle)
      const hasManualPreference = localStorage.getItem('theme_manual');
      if (!hasManualPreference) {
        setTheme(e.matches ? 'dark' : 'light');
      }
    };
    
    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Legacy browsers
    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  const toggleTheme = () => {
    // Mark that user has manually set preference
    localStorage.setItem('theme_manual', 'true');
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  // Reset to system preference
  const useSystemTheme = () => {
    localStorage.removeItem('theme_manual');
    localStorage.removeItem('theme');
    if (typeof window !== 'undefined' && window.matchMedia) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setTheme(prefersDark ? 'dark' : 'light');
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, useSystemTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};